// noticer.ts — the imperative narration channel for the Noticer (StatusCorner).
// The onboarding conductor uses this to make the corner *speak*: a sticky line
// with optional action buttons ("next →", "skip"). It rides the same window-event
// grammar as the existing `innernet:clip` flashes, so nothing else changes.
//
//   narrate({ id, text, actions: [{ label: "next →", act: "next" }], sticky: true })
//   narrateClear()   // hand the corner back to hover-hints
//
// A clicked action re-emits `innernet:narrate-act` with its `act` id; the
// conductor listens for that to advance. Kept tiny + framework-free so both the
// dispatcher (conductor) and the receiver (StatusCorner) share one contract.

export interface NoticerAction {
  label: string;
  /** semantic id the conductor interprets (next · skip · goto:/x · unlock:x · create · import · email). */
  act: string;
}

export interface NarrateDetail {
  /** stable id for the current line (so repeat dispatches of the same beat no-op). */
  id: string;
  text: string;
  actions?: NoticerAction[];
  /** sticky lines stay until cleared or replaced (default true for narration). */
  sticky?: boolean;
}

export const NARRATE_EVENT = "innernet:narrate";
export const NARRATE_ACT_EVENT = "innernet:narrate-act";

// The line currently being spoken. Events alone lose the first beat when a
// receiver mounts in the same commit the speaker fires (effects run in tree
// order — the conductor speaks before a freshly-mounted guide can listen), so
// the channel remembers what is being said and a late subscriber reads it.
let current: NarrateDetail | null = null;

/** What the channel is saying right now — for receivers that mounted late. */
export function currentNarration(): NarrateDetail | null {
  return current;
}

export function narrate(detail: NarrateDetail) {
  if (typeof window === "undefined") return;
  current = detail;
  window.dispatchEvent(new CustomEvent(NARRATE_EVENT, { detail }));
}

/** Clear the current narration and return the corner to hover-hint duty. */
export function narrateClear() {
  if (typeof window === "undefined") return;
  current = null;
  window.dispatchEvent(new CustomEvent(NARRATE_EVENT, { detail: null }));
}

/** Fire an action the user clicked in the corner (or an auto-advance). */
export function narrateAct(act: string) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(NARRATE_ACT_EVENT, { detail: { act } }));
}
